import { useEffect, useRef } from "react"

// Declared full-loop duration. The two-stage demo completes comfortably within
// this; the feature browser reads it to time how long the Write tab stays active
// (so the demo is never cut short). Keep >= the demo's natural completion.
export const WRITE_CYCLE_MS = 17000

// Animated mini-mockup for the Write feature row.
// Two-stage loop (~14s):
//   Stage 1 — Planning: AI panel only (full card). User types "Plan this section"
//             into the chat input → bubble → activity steps run → plan streams
//             inline → Accept/Revise (neutral) → Accept auto-fires.
//   Stage 2 — Drafting: AI panel collapses out, editor takes the whole card.
//             Two paragraphs stream into the editor, then a shimmering skeleton
//             paragraph reveals line-by-line to suggest more is being drafted.
// Styles live under `.wr-*` classes in src/index.css (scoped to .landing-page).

const STEPS = [
  "Reading tender requirements",
  "Pulling evaluation criteria",
  "Aligning with win themes",
  "Reading bid brief",
] as const

const PLAN_INTRO =
  "Based on the tender requirements, evaluation criteria, your win themes and library evidence, here's a 4-point plan:"

const PLAN_ITEMS = [
  { num: "1.", heading: "Delivery model & oversight", source: "— 5.2a, brief" },
  { num: "2.", heading: "Phased delivery, go/no-go gates", source: "— eval: programme" },
  { num: "3.", heading: "Team & supply chain", source: "— 5.2c, local supply" },
  { num: "4.", heading: "Risk register, zero-LTI evidence", source: "— eval: risk" },
] as const

const PARAGRAPHS = [
  "We propose a fully integrated delivery model led by senior engineering oversight, with embedded SME teams from day one. The approach pairs deep highway-construction experience, proven on the M5 Bridge Upgrade.",
  "Works are structured into four phases: mobilisation, earthworks and drainage, pavement and structures, and commissioning. Each phase has defined go/no-go gates with independent quality holds. Long-lead procurement of precast units and asphalt binder is decoupled from the critical path through early-release ordering, the same approach that delivered the M5 upgrade six weeks ahead of programme.",
] as const

const BURST_SVG =
  '<svg viewBox="0 0 100 100" fill="none"><g stroke="currentColor" stroke-width="11" stroke-linecap="round"><line x1="50" y1="35" x2="50" y2="13"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(45 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(90 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(135 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(180 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(225 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(270 50 50)"/><line x1="50" y1="35" x2="50" y2="13" transform="rotate(315 50 50)"/></g></svg>'
const CHECK_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>'

export function WriteVisual() {
  const rootRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const promptRef = useRef<HTMLSpanElement>(null)
  const userBubbleRef = useRef<HTMLDivElement>(null)
  const activityRef = useRef<HTMLDivElement>(null)
  const stepListRef = useRef<HTMLDivElement>(null)
  const planBlockRef = useRef<HTMLDivElement>(null)
  const planIntroRef = useRef<HTMLSpanElement>(null)
  const planLine1Ref = useRef<HTMLSpanElement>(null)
  const planLine2Ref = useRef<HTMLSpanElement>(null)
  const planLine3Ref = useRef<HTMLSpanElement>(null)
  const planLine4Ref = useRef<HTMLSpanElement>(null)
  const planActionsRef = useRef<HTMLDivElement>(null)
  const acceptBtnRef = useRef<HTMLButtonElement>(null)
  const aiBodyRef = useRef<HTMLDivElement>(null)
  const editorBodyRef = useRef<HTMLDivElement>(null)
  const para1Ref = useRef<HTMLParagraphElement>(null)
  const para2Ref = useRef<HTMLParagraphElement>(null)
  const moreLinesRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    let timers: ReturnType<typeof setTimeout>[] = []
    let isPlaying = false
    let isInView = false

    const tt = (fn: () => void, ms: number) => {
      const t = setTimeout(fn, ms)
      timers.push(t)
      return t
    }
    const clear = () => {
      timers.forEach((t) => clearTimeout(t))
      timers = []
    }

    const planLines = [planLine1Ref.current, planLine2Ref.current, planLine3Ref.current, planLine4Ref.current]

    const autoScrollAi = () => {
      const el = aiBodyRef.current
      if (el) el.scrollTop = el.scrollHeight
    }
    const autoScrollEditor = () => {
      const el = editorBodyRef.current
      if (el) el.scrollTop = el.scrollHeight
    }

    function typeInto(el: HTMLElement, text: string, delay: number, done?: () => void, scroll?: "ai" | "editor") {
      let i = 0
      function step() {
        if (!isPlaying) return
        if (i < text.length) {
          el.textContent = text.slice(0, i + 1)
          i++
          if (scroll === "ai") autoScrollAi()
          else if (scroll === "editor") autoScrollEditor()
          tt(step, delay)
        } else if (done) done()
      }
      step()
    }

    function renderStep(idx: number, status: "running" | "done") {
      const list = stepListRef.current
      if (!list) return
      let row = list.children[idx] as HTMLElement | undefined
      const text = STEPS[idx]
      const iconHTML = status === "running" ? BURST_SVG : CHECK_SVG
      if (!row) {
        row = document.createElement("div")
        row.className = "wr-step"
        row.innerHTML = `<span class="wr-step-icon ${status} ${status === "running" ? "wr-breathe" : ""}">${iconHTML}</span><div>${text}</div>`
        list.appendChild(row)
        requestAnimationFrame(() => row?.classList.add("show"))
      } else {
        const icon = row.querySelector(".wr-step-icon")
        if (icon) {
          icon.className = `wr-step-icon ${status} ${status === "running" ? "wr-breathe" : ""}`
          icon.innerHTML = iconHTML
        }
      }
    }

    function reset() {
      clear()
      frameRef.current?.classList.remove("drafting")
      if (promptRef.current) {
        promptRef.current.textContent = "Ask anything…"
        promptRef.current.className = "wr-input-text wr-placeholder wr-typing"
      }
      userBubbleRef.current?.classList.remove("show")
      activityRef.current?.classList.remove("show")
      if (stepListRef.current) stepListRef.current.innerHTML = ""
      planBlockRef.current?.classList.remove("show")
      if (planIntroRef.current) planIntroRef.current.textContent = ""
      planLines.forEach((l) => {
        if (l) l.innerHTML = ""
      })
      planActionsRef.current?.classList.remove("show")
      acceptBtnRef.current?.classList.remove("pressed")
      if (para1Ref.current) para1Ref.current.textContent = ""
      if (para2Ref.current) para2Ref.current.textContent = ""
      moreLinesRef.current?.classList.remove("show")
    }

    function play() {
      if (!isPlaying) return
      reset()
      isPlaying = true

      // Restart the whole loop on a fixed, declared cycle so the feature browser
      // can time it precisely. The demo below completes before this fires.
      tt(() => play(), WRITE_CYCLE_MS)

      // 1) Type "Plan this section" into the chat input
      tt(() => {
        if (!promptRef.current) return
        promptRef.current.classList.remove("wr-placeholder")
        promptRef.current.textContent = ""
        typeInto(promptRef.current, "Plan this section", 38, () => {
          // 2) Bubble appears + input clears
          tt(() => {
            if (promptRef.current) {
              promptRef.current.textContent = "Ask anything…"
              promptRef.current.classList.add("wr-placeholder")
            }
            userBubbleRef.current?.classList.add("show")
            autoScrollAi()

            // 3) Activity block + step 1 running
            tt(() => {
              activityRef.current?.classList.add("show")
              renderStep(0, "running")
              autoScrollAi()
            }, 300)

            // Steps complete + next runs
            const stepTimings = [700, 1300, 1900, 2500]
            stepTimings.forEach((t, i) => {
              tt(() => {
                renderStep(i, "done")
                if (i + 1 < STEPS.length) renderStep(i + 1, "running")
                autoScrollAi()
              }, 300 + t)
            })

            // 4) Plan streams inline
            tt(() => {
              planBlockRef.current?.classList.add("show")
              autoScrollAi()
              if (!planIntroRef.current) return
              typeInto(
                planIntroRef.current,
                PLAN_INTRO,
                12,
                () => {
                  autoScrollAi()
                  const streamLine = (i: number) => {
                    autoScrollAi()
                    if (i >= PLAN_ITEMS.length) {
                      // 5) Show accept/revise, then auto-accept
                      tt(() => {
                        planActionsRef.current?.classList.add("show")
                        autoScrollAi()
                        tt(() => {
                          acceptBtnRef.current?.classList.add("pressed")
                          tt(() => {
                            // 6) Collapse AI panel, editor takes the card
                            frameRef.current?.classList.add("drafting")
                            tt(() => {
                              // 7) Stream paragraphs
                              if (!para1Ref.current || !para2Ref.current) return
                              typeInto(
                                para1Ref.current,
                                PARAGRAPHS[0],
                                7,
                                () => {
                                  tt(() => {
                                    if (!para2Ref.current) return
                                    typeInto(
                                      para2Ref.current,
                                      PARAGRAPHS[1],
                                      6,
                                      () => {
                                        // 8) Reveal next-paragraph skeleton line-by-line
                                        tt(() => {
                                          moreLinesRef.current?.classList.add("show")
                                          autoScrollEditor()
                                        }, 250)
                                        tt(autoScrollEditor, 800)
                                        tt(autoScrollEditor, 1500)
                                        tt(autoScrollEditor, 2200)
                                        tt(autoScrollEditor, 2900)
                                        // (restart is now a single top-level timer at the start of play)
                                      },
                                      "editor",
                                    )
                                  }, 200)
                                },
                                "editor",
                              )
                            }, 500)
                          }, 350)
                        }, 700)
                      }, 350)
                      return
                    }
                    const line = planLines[i]
                    const item = PLAN_ITEMS[i]
                    if (!line || !item) return
                    line.innerHTML = `<span class="wr-plan-num">${item.num}</span><span class="wr-plan-heading"></span> <span class="wr-plan-source"></span>`
                    const headingEl = line.querySelector<HTMLElement>(".wr-plan-heading")
                    const sourceEl = line.querySelector<HTMLElement>(".wr-plan-source")
                    if (!headingEl || !sourceEl) return
                    typeInto(
                      headingEl,
                      item.heading,
                      10,
                      () => {
                        typeInto(
                          sourceEl,
                          " " + item.source,
                          10,
                          () => {
                            autoScrollAi()
                            tt(() => streamLine(i + 1), 70)
                          },
                          "ai",
                        )
                      },
                      "ai",
                    )
                  }
                  streamLine(0)
                },
                "ai",
              )
            }, 300 + (stepTimings[stepTimings.length - 1] ?? 0) + 350)
          }, 320)
        })
      }, 600)
    }

    function start() {
      if (isPlaying || reduced) return
      isPlaying = true
      play()
    }
    function stop() {
      isPlaying = false
      clear()
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        const wasInView = isInView
        isInView = entry.isIntersecting
        if (isInView && !wasInView) start()
        else if (!isInView && wasInView) stop()
      },
      { threshold: 0.35 },
    )
    io.observe(root)

    return () => {
      io.disconnect()
      stop()
    }
  }, [])

  return (
    <div ref={rootRef}>
      <div ref={frameRef} className="wr-frame">
        {/* ── Stage 1: Planning (AI panel only) ── */}
        <div className="wr-stage wr-stage-planning">
          <div className="wr-head">Writing Assistant</div>
          <div className="wr-ctx">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span className="wr-ctx-name">2. Methodology</span>
          </div>

          <div ref={aiBodyRef} className="wr-body">
            <div ref={userBubbleRef} className="wr-user">Plan this section</div>

            <div ref={activityRef} className="wr-activity">
              <div className="wr-activity-head">
                <span className="wr-activity-spark wr-breathe">
                  <svg viewBox="0 0 100 100" fill="none">
                    <g stroke="currentColor" strokeWidth={11} strokeLinecap="round">
                      <line x1="50" y1="35" x2="50" y2="13" />
                      <line x1="50" y1="35" x2="50" y2="13" transform="rotate(45 50 50)" />
                      <line x1="50" y1="35" x2="50" y2="13" transform="rotate(90 50 50)" />
                      <line x1="50" y1="35" x2="50" y2="13" transform="rotate(135 50 50)" />
                      <line x1="50" y1="35" x2="50" y2="13" transform="rotate(180 50 50)" />
                      <line x1="50" y1="35" x2="50" y2="13" transform="rotate(225 50 50)" />
                      <line x1="50" y1="35" x2="50" y2="13" transform="rotate(270 50 50)" />
                      <line x1="50" y1="35" x2="50" y2="13" transform="rotate(315 50 50)" />
                    </g>
                  </svg>
                </span>
                <span className="wr-activity-text">Planning Methodology</span>
              </div>
              <div ref={stepListRef} className="wr-steps" />
            </div>

            <div ref={planBlockRef} className="wr-plan">
              <span ref={planIntroRef} className="wr-plan-line wr-plan-intro" />
              <span ref={planLine1Ref} className="wr-plan-line" />
              <span ref={planLine2Ref} className="wr-plan-line" />
              <span ref={planLine3Ref} className="wr-plan-line" />
              <span ref={planLine4Ref} className="wr-plan-line" />
            </div>

            <div ref={planActionsRef} className="wr-actions">
              <button ref={acceptBtnRef} type="button" className="wr-btn wr-btn-accept">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Accept plan
              </button>
              <button type="button" className="wr-btn">Revise</button>
            </div>

            <div className="wr-spacer" />
          </div>

          <div className="wr-input-wrap">
            <div className="wr-input">
              <span ref={promptRef} className="wr-input-text wr-placeholder wr-typing">Ask anything…</span>
              <button type="button" className="wr-send" aria-label="Send">
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </button>
            </div>
            <div className="wr-input-tools">
              <button type="button" aria-label="Attach">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>
              <button type="button">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="13" x2="12" y2="17" />
                  <line x1="10" y1="15" x2="14" y2="15" />
                </svg>
                Add evidence
              </button>
            </div>
          </div>
        </div>

        {/* ── Stage 2: Editor (full-card takeover) ── */}
        <div className="wr-stage wr-stage-editor">
          <div className="wr-editor-head">
            <div className="wr-editor-title-wrap">
              <span className="wr-editor-title">2. Methodology</span>
              <span className="wr-status-pill">Drafting</span>
            </div>
            <button type="button" className="wr-btn">Mark complete</button>
          </div>
          <div ref={editorBodyRef} className="wr-editor-body">
            <p ref={para1Ref} />
            <p ref={para2Ref} />
            <div ref={moreLinesRef} className="wr-more">
              <div className="wr-more-skel wr-w1" />
              <div className="wr-more-skel wr-w2" />
              <div className="wr-more-skel wr-w3" />
              <div className="wr-more-skel wr-w4" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
